import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { JobExportStatusResponse } from '@cartwhl/common/api/getJobExportStatus';
import type { MotionStatus } from '@cartwhl/common/api/getMotionsByGroup';

import {
  exportSlotsHaveReadyUrl,
  type MotionExportURLSlot,
} from './batchMotionExportUrls';

/** Rows that carry studio / CWMS preview URLs from GET /motions/{batch}. */
export type MotionPreviewPollRow = {
  status?: MotionStatus;
  studioPreviewURL?: string;
  cwmsURL?: string;
};

/** True when at least one COMPLETED job has no preview URL yet (MP4 may still be rendering). */
export function completedJobsMissingPreviewUrl(
  files: MotionPreviewPollRow[],
): boolean {
  return files.some(
    (f) =>
      f.status === 'COMPLETED' &&
      !(f.studioPreviewURL?.trim() || f.cwmsURL?.trim()),
  );
}

/** Max extra polls after batch is COMPLETED/FAILED while previews are still missing (~3 min at 15s). */
export const MOTION_PREVIEW_CATCH_UP_POLLS = 12;

/**
 * Stable key for presigned URLs that only differ by query string (avoids video remounts).
 */
export function mediaUrlPathKey(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * MIME type for batch source-video overlay `<source type>` from API file extension.
 */
export function videoMimeTypeFromExtension(ext: string | undefined): string {
  switch (ext?.toLowerCase().replace(/^\./, '')) {
    case 'webm':
      return 'video/webm';
    case 'mov':
      return 'video/quicktime';
    case 'm4v':
      return 'video/x-m4v';
    default:
      return 'video/mp4';
  }
}

/** Clears focus before opening a modal so Radix does not warn on aria-hidden ancestors. */
export function blurFocusedElement(): void {
  const el = document.activeElement;
  if (el instanceof HTMLElement) {
    el.blur();
  }
}

type SourceVideoMedia = { url: string; fileExtension: string };

/**
 * Loads comic reference video metadata for the preview overlay (cached per mediaId).
 *
 * @param getSourceVideoMedia - store method wrapping GET /media/{id}
 * @param sourceVideoMediaId - from motion row when preview is open
 */
export function useComicSourceVideoOverlay(
  getSourceVideoMedia: (mediaId: string) => Promise<SourceVideoMedia>,
  sourceVideoMediaId: string | undefined,
): SourceVideoMedia | undefined {
  const [overlay, setOverlay] = useState<SourceVideoMedia | undefined>();
  const cacheRef = useRef<Map<string, SourceVideoMedia>>(new Map());

  useEffect(() => {
    const mediaId = sourceVideoMediaId?.trim();
    if (!mediaId) {
      setOverlay(undefined);
      return;
    }

    const cached = cacheRef.current.get(mediaId);
    if (cached) {
      setOverlay(cached);
      return;
    }

    let cancelled = false;
    void getSourceVideoMedia(mediaId)
      .then((media) => {
        if (cancelled) return;
        cacheRef.current.set(mediaId, media);
        setOverlay(media);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(
            '[BatchComic] Failed to load source video for preview overlay:',
            err,
          );
          setOverlay(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [getSourceVideoMedia, sourceVideoMediaId]);

  return overlay;
}

type FileWithJobStatus = {
  id: string;
  status?: MotionStatus;
  exportURLs?: MotionExportURLSlot[];
};

/**
 * Enables per-thumbnail download when export presigned URLs are ready, or (legacy)
 * when zip export-status reports ready.
 */
export function useThumbnailDownloadGate(
  processId: string | null,
  files: readonly FileWithJobStatus[],
  pollJobExportStatus: (jobId: string) => Promise<JobExportStatusResponse>,
) {
  const completedJobIdsForZipPoll = useMemo(
    () =>
      files
        .filter((f) => f.status === 'COMPLETED' && !f.exportURLs?.length)
        .map((f) => f.id),
    [files],
  );

  const downloadReadyJobIds = useJobDownloadReadiness(
    processId,
    completedJobIdsForZipPoll,
    pollJobExportStatus,
  );

  const canDownloadFile = useCallback(
    (file: FileWithJobStatus) => {
      if (file.exportURLs?.length) {
        return exportSlotsHaveReadyUrl(file.exportURLs);
      }
      return downloadReadyJobIds.has(file.id);
    },
    [downloadReadyJobIds],
  );

  return { canDownloadFile, downloadReadyJobIds };
}

/** Matches preview modal export-status polling interval. */
const JOB_EXPORT_POLL_MS = 5000;

/**
 * Polls GET .../export-status for completed jobs until `downloadExportsReady`
 * (same gate as BatchMotionJobPreviewModal download controls).
 *
 * @param processId - batch ID; polling resets when this changes
 * @param completedJobIds - job IDs in COMPLETED status
 * @param pollJobExportStatus - typically store.pollJobZipExportReadiness
 */
export function useJobDownloadReadiness(
  processId: string | null,
  completedJobIds: readonly string[],
  pollJobExportStatus: (jobId: string) => Promise<JobExportStatusResponse>,
): ReadonlySet<string> {
  const [readyJobIds, setReadyJobIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const failedJobIdsRef = useRef<Set<string>>(new Set());
  const readyJobIdsRef = useRef(readyJobIds);
  readyJobIdsRef.current = readyJobIds;

  const completedKey = completedJobIds.join(',');

  useEffect(() => {
    failedJobIdsRef.current = new Set();
    setReadyJobIds(new Set());
  }, [processId]);

  const poll = useCallback(async () => {
    const pending = completedJobIds.filter(
      (id) =>
        !failedJobIdsRef.current.has(id) && !readyJobIdsRef.current.has(id),
    );
    if (pending.length === 0) return;

    const updates: {
      jobId: string;
      ready: boolean;
      failed: boolean;
    }[] = [];

    await Promise.all(
      pending.map(async (jobId) => {
        try {
          const r = await pollJobExportStatus(jobId);
          updates.push({
            jobId,
            ready: r.downloadExportsReady,
            failed: r.hasFailedExport,
          });
        } catch (err) {
          console.warn(
            '[batch] export-status poll failed for job',
            jobId,
            err,
          );
        }
      }),
    );

    for (const { jobId, failed } of updates) {
      if (failed) failedJobIdsRef.current.add(jobId);
    }

    setReadyJobIds((prev) => {
      const next = new Set(prev);
      for (const { jobId, ready } of updates) {
        if (ready) next.add(jobId);
      }
      for (const id of next) {
        if (!completedJobIds.includes(id)) next.delete(id);
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, [completedJobIds, pollJobExportStatus]);

  useEffect(() => {
    if (!processId || completedJobIds.length === 0) {
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const hasPending = () =>
      completedJobIds.some(
        (id) =>
          !failedJobIdsRef.current.has(id) && !readyJobIdsRef.current.has(id),
      );

    const run = () => {
      if (cancelled) return;
      if (!hasPending()) {
        if (intervalId !== undefined) {
          clearInterval(intervalId);
          intervalId = undefined;
        }
        return;
      }
      void poll();
    };

    void run();
    intervalId = setInterval(run, JOB_EXPORT_POLL_MS);
    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, [processId, completedKey, poll]);

  return readyJobIds;
}
